import { z } from 'zod';
import { idListQuery, queryBoolean } from './common';
import { dateRangeQuerySchema } from './finance';

/**
 * The analytics contract: what can be asked, and what an answer looks like.
 *
 * ## The one idea the whole file is built on
 *
 * A number on an analytics screen can be in four states, and three of them are
 * not "a number":
 *
 *   known, and here            the ordinary case
 *   known, but not for you     withheld by role — a technician and revenue
 *   not knowable from the data the schema cannot answer it
 *   genuinely zero             which is a *fact*, not an absence
 *
 * `income-grouping.ts` already discovered this the hard way: payment in this
 * system is recorded per visit and never per report, so "collected, by test"
 * has no answer — and printing `0` there reads as "nobody paid" rather than
 * "this cannot be known". It hides the column instead.
 *
 * Every metric here carries that distinction in its type, so a screen cannot
 * accidentally render an unknown as a zero. `Metric.known: false` means the
 * data cannot say; `value: null` with `known: true` means the caller may not
 * see it; `value: 0` means zero.
 */

/* ------------------------------------------------------------------------- *
 * Query
 * ------------------------------------------------------------------------- */

export const analyticsGranularitySchema = z.enum(['day', 'week', 'month']);
export type AnalyticsGranularity = z.infer<typeof analyticsGranularitySchema>;

/**
 * What "compared with" means.
 *
 * `previous` is the same number of days immediately before the range — the
 * right comparison for "is this week better than last week". `year` is the same
 * calendar dates a year earlier, which is the right one for anything seasonal,
 * and which is *not* the same as subtracting 365 days: that slides every
 * comparison by a weekday and makes a Monday-heavy lab look like it changed.
 */
export const analyticsCompareSchema = z.enum(['none', 'previous', 'year']);
export type AnalyticsCompare = z.infer<typeof analyticsCompareSchema>;

export const COMPARE_LABELS = {
  none: 'No comparison',
  previous: 'Previous period',
  year: 'Same period last year',
} as const satisfies Record<AnalyticsCompare, string>;

/**
 * The longest span a section will compute.
 *
 * Not really a performance guard — Postgres handles a decade of this lab's
 * volume without complaint. It is an honesty guard: a "daily" series over five
 * years is 1,800 points in a 700-pixel chart, which is a texture, not a trend.
 * Past this the caller should be asking for months.
 */
const MAX_ANALYTICS_SPAN_DAYS = 400;

/**
 * The filters every section understands.
 *
 * Spread rather than `.extend`ed by each endpoint, because a schema that has
 * had `.refine` applied is a `ZodEffects` and no longer has `.extend`. Building
 * from a shared field object lets each endpoint add its own fields *and* apply
 * the shared refinements last.
 */
const ANALYTICS_FILTER_FIELDS = {
  ...dateRangeQuerySchema.shape,
  department_ids: idListQuery.optional(),
  test_ids: idListQuery.optional(),
  panel_ids: idListQuery.optional(),
  user_ids: idListQuery.optional(),
  /** Absent means "pick one from the span" — see `granularityFor`. Echoed back. */
  granularity: analyticsGranularitySchema.optional(),
  compare: analyticsCompareSchema.default('none'),
  /**
   * Include visits whose date was reconstructed from a year alone.
   *
   * An Excel import of a paper ledger writes these on `YYYY-07-01`, so they are
   * a real July spike in any daily series and a real answer in an annual total.
   * The default therefore differs by *shape* rather than by opinion: series
   * exclude them, totals include them and say how many were involved.
   */
  include_approximate: queryBoolean.default(false),
} as const;

const DAY_MS = 86_400_000;

export function spanDays(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  return Math.round((end - start) / DAY_MS) + 1;
}

const withRangeRules = <T extends z.ZodType<{ from: string; to: string }>>(schema: T) =>
  schema
    .refine((filters) => filters.from <= filters.to, {
      message: 'The start date must not be after the end date',
      path: ['from'],
    })
    .refine((filters) => spanDays(filters.from, filters.to) <= MAX_ANALYTICS_SPAN_DAYS, {
      message: `Choose a range of ${MAX_ANALYTICS_SPAN_DAYS} days or fewer`,
      path: ['to'],
    });

export const analyticsFiltersSchema = withRangeRules(z.object(ANALYTICS_FILTER_FIELDS));
export type AnalyticsFilters = z.infer<typeof analyticsFiltersSchema>;

/** The sections, and the dimension each one defaults to. */
export type AnalyticsSection =
  'overview' | 'volume' | 'patients' | 'flow' | 'quality' | 'staff' | 'supply';

/** How far ahead the supply section's expiry warning looks. */
export const EXPIRY_HORIZON_DAYS = 90;

export const SECTION_LABELS = {
  overview: 'Overview',
  volume: 'Volume',
  patients: 'Patients',
  flow: 'Flow',
  quality: 'Quality',
  staff: 'Staff',
  supply: 'Supply',
} as const satisfies Record<AnalyticsSection, string>;

/**
 * The dimensions each section can be broken down by.
 *
 * Shared rather than client-side so the section nav can carry a query string
 * across a switch and drop anything the target does not understand — smuggling
 * `by=stage` onto a screen with no stages is how a filter silently does nothing.
 */
export const SECTION_DIMENSIONS = {
  overview: ['day'],
  volume: ['test', 'department', 'panel', 'hour', 'weekday'],
  patients: ['age', 'gender', 'returning', 'referrer'],
  flow: ['stage', 'test', 'department'],
  quality: ['test', 'parameter', 'flag'],
  staff: ['performed', 'verified', 'registered'],
  supply: ['item', 'department'],
} as const satisfies Record<AnalyticsSection, readonly string[]>;

export const DIMENSION_LABELS: Record<string, string> = {
  day: 'By day',
  test: 'By test',
  department: 'By bench',
  panel: 'By panel',
  hour: 'By hour',
  weekday: 'By weekday',
  age: 'By age',
  gender: 'By gender',
  returning: 'New vs returning',
  referrer: 'By referrer',
  stage: 'By stage',
  parameter: 'By analyte',
  flag: 'By flag',
  performed: 'Results entered',
  verified: 'Reports verified',
  registered: 'Visits registered',
  item: 'By reagent',
};

function defaultDimension(section: AnalyticsSection): string {
  return SECTION_DIMENSIONS[section][0];
}

export function dimensionFor(section: AnalyticsSection, requested: string | undefined): string {
  const allowed: readonly string[] = SECTION_DIMENSIONS[section];
  // Guarded `includes`, never a cast: a hand-edited URL must not put an unknown
  // value on the wire. Same rule `parseFilters` follows in the worklist.
  return requested && allowed.includes(requested) ? requested : defaultDimension(section);
}

export const sectionQuerySchema = withRangeRules(
  z.object({ ...ANALYTICS_FILTER_FIELDS, by: z.string().optional() }),
);
export type SectionQuery = z.infer<typeof sectionQuerySchema>;

/* ------------------------------------------------------------------------- *
 * Response primitives
 * ------------------------------------------------------------------------- */

export type MetricUnit = 'count' | 'money' | 'percent' | 'hours' | 'days' | 'ratio';

/**
 * Which way is good.
 *
 * Overdue rising is bad; revenue rising is good; a median age rising is neither.
 * Carried on the wire because the *server* knows what the metric means and the
 * tile does not — and colouring a delta green because the arrow points up is
 * how a dashboard ends up congratulating a lab on its growing backlog.
 */
export type MetricDirection = 'up_is_good' | 'down_is_good' | 'neutral';

export interface MetricDelta {
  basis: Exclude<AnalyticsCompare, 'none'>;
  /** The comparison period's own value, so the client never recomputes it. */
  previous: number | null;
  change: number | null;
  /**
   * Fractional change, -1 upward.
   *
   * Null when `previous` is 0. An infinite percentage rise from nothing is
   * noise dressed as a headline — "up 100%" from one visit to two is true and
   * useless.
   */
  change_ratio: number | null;
  direction: MetricDirection;
}

export interface Metric {
  key: string;
  label: string;
  value: number | null;
  unit: MetricUnit;
  /** False when the data cannot answer. `caveat` then says why. */
  known: boolean;
  /** Shown instead of the number when the value is zero. Never a bare "0". */
  zero_label?: string;
  /** Where the tile drills through to, already filtered. */
  href: string | null;
  delta: MetricDelta | null;
  caveat: string | null;
}

export interface SeriesPoint {
  /** `YYYY-MM-DD` for day and week buckets, `YYYY-MM` for months. */
  bucket: string;
  label: string;
  /** Null is a gap — a period with no data, which is not a period with zero. */
  value: number | null;
}

export interface AnalyticsSeries {
  key: string;
  label: string;
  unit: MetricUnit;
  granularity: AnalyticsGranularity;
  points: SeriesPoint[];
  /** Index-aligned with `points`. Empty when `compare` is `none`. */
  compare: SeriesPoint[];
}

export interface CategoryDatum {
  key: string;
  label: string;
  value: number;
  /** Against the period total, for a mix chart. */
  share: number;
  /** A second measure for the same category — money beside a count. */
  secondary: number | null;
  href: string | null;
}

/**
 * A spread of durations.
 *
 * Median and p90 rather than a mean, because turnaround is skewed: one report
 * that sat over a weekend drags a mean somewhere no individual report went.
 * The mean is returned as well, so the gap between it and the median is
 * visible — that gap *is* the skew.
 */
export interface Distribution {
  p50: number | null;
  p90: number | null;
  mean: number | null;
  min: number | null;
  max: number | null;
  n: number;
  /**
   * Rows this metric could not speak for, and why.
   *
   * Rendered, not hidden. A turnaround compliance figure whose denominator
   * quietly dropped every test with no committed turnaround is a different
   * number from the one it claims to be.
   */
  excluded: { reason: string; count: number }[];
}

export interface Histogram {
  bins: { from: number; to: number; count: number }[];
  below: number;
  above: number;
  n: number;
}

/** Carried by every section payload. */
export interface AnalyticsEnvelope {
  section: AnalyticsSection;
  from: string;
  to: string;
  by: string;
  granularity: AnalyticsGranularity;
  compare: AnalyticsCompare;
  compare_range: { from: string; to: string } | null;
  generated_at: string;
  /**
   * Caveats that apply to the whole payload.
   *
   * Shown as a strip on the screen rather than swallowed — the same decision
   * `ProfitLoss.missing` makes. A screen that quietly omits a qualification is
   * worse than one that never claimed to be complete.
   */
  notes: string[];
}

/** The option lists the filter bar needs, in one call. */
export interface AnalyticsFilterOptions {
  departments: { id: number; name: string }[];
  tests: { id: number; name: string }[];
  panels: { id: number; name: string }[];
  users: { id: number; name: string }[];
  /** Earliest visit on record, so the date picker can bound itself honestly. */
  earliest: string | null;
}
